import { useCallback, useEffect, useMemo, useRef } from 'react';

import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import dateFormats from 'month-view-page-common/dateFormats';
import { useSelector } from 'react-redux';
import { redirect } from 'saddlebag-browser';

import { formatDateNonlocalised } from '../../../../bootstrap/localisation';
import getFeatureDecision from '../../../../config';
import { useMediaQuery } from '../../helpers/hooks/useMediaQuery';
import { trackInteractionEvent } from '../../helpers/logging/trackInteractionEvent';
import {
  getTripDuration,
  getSelectedOutboundDate,
  getSelectedInboundDate,
  getStay,
  getPreferDirects,
} from '../../selectors';
import { getDealHunterSessionForDuration } from '../../selectors/dealHunter';
import getSelectedDaysPrice from '../../selectors/selectedDaysPrice';

import { convertItineraryPriceToWhole } from './convertItineraryPriceToWhole';
import { mapItineraryToCardProps } from './mapItineraryToCardProps';

import type { ItineraryCardProps } from './ItineraryCard';
import type { DealHunterSession } from '../../selectors/dealHunterTypes';
import type { State } from '../../selectors/types';

export interface UseItineraryCardDataReturn {
  itineraryCardProps: ItineraryCardProps | null;
  showLoading: boolean;
  showCard: boolean;
  isSessionActive: boolean;
  pollCount: number;
  shouldShowItineraryCard: boolean;
  handleShowDetails: (id: string | undefined) => void;
  handleCardClick: (pollingComplete: boolean) => void;
}

function getEventNumbers(
  indicativePrice: number | null,
  preferDirects: boolean,
  dealHunterSession?: DealHunterSession | null,
) {
  const itinerary = preferDirects
    ? dealHunterSession?.cheapestDirectItinerary
    : dealHunterSession?.cheapestItinerary;

  const outboundStops = itinerary?.legs?.[0]?.stopCount;
  const inboundStops = itinerary?.legs?.[1]?.stopCount;
  const itineraryPrice = convertItineraryPriceToWhole(itinerary?.quote?.price);

  const numbers: Record<string, number> = {
    ...(outboundStops != null && { outboundStops }),
    ...(inboundStops != null && { inboundStops }),
    ...(itineraryPrice != null && { itineraryPrice }),
    ...(indicativePrice != null && { indicativePrice }),
  };
  return numbers;
}

export const useItineraryCardData = (): UseItineraryCardDataReturn => {
  const isMobilePhone = useMediaQuery(BREAKPOINTS.MOBILE);
  const tripDuration = useSelector(getTripDuration);
  const outboundDate = useSelector(getSelectedOutboundDate);
  const inboundDate = useSelector(getSelectedInboundDate);
  const dealHunterSession = useSelector((state: State) =>
    getDealHunterSessionForDuration(state, tripDuration),
  );
  const { adults, childrenAges } = useSelector(getStay);
  const preferDirects = useSelector(getPreferDirects);

  const shouldShowItineraryCard = Boolean(
    getFeatureDecision('ENABLE_DEAL_HUNTER_LITE', false),
  );

  const itineraryCardProps = useMemo(() => {
    const itinerary = preferDirects
      ? dealHunterSession?.cheapestDirectItinerary
      : dealHunterSession?.cheapestItinerary;
    if (!itinerary) return null;
    return mapItineraryToCardProps(itinerary);
  }, [dealHunterSession, preferDirects]);

  const isRadarSessionActive =
    shouldShowItineraryCard &&
    dealHunterSession != null &&
    dealHunterSession.status === 'incomplete';

  const showLoading = Boolean(isRadarSessionActive && !itineraryCardProps);
  const showCard = Boolean(itineraryCardProps);
  const pollCount = dealHunterSession?.pollCount ?? 0;
  const indicativePrice = useSelector(getSelectedDaysPrice);

  const previousStatusRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentStatus = dealHunterSession?.status;
    const wasNotComplete = previousStatusRef.current !== 'complete';
    previousStatusRef.current = currentStatus;

    if (currentStatus === 'complete' && wasNotComplete) {
      const numbers = getEventNumbers(
        indicativePrice,
        preferDirects,
        dealHunterSession,
      );
      trackInteractionEvent({
        name: 'Itinerary_Loading_Complete',
        strings: {
          ...(tripDuration != null && {
            tripDuration: tripDuration.toString(),
          }),
        },
        numbers: {
          ...numbers,
          pollCount,
        },
        bools: {
          preferDirect: preferDirects,
        },
      });
    }
  }, [
    dealHunterSession,
    tripDuration,
    preferDirects,
    indicativePrice,
    pollCount,
  ]);

  const getRedirectUrl = useCallback(
    (itineraryId: string | undefined): string | null => {
      const origin = itineraryCardProps?.outbound.departureStation;
      const destination = itineraryCardProps?.outbound.arrivalStation;
      const formattedOutboundDate = formatDateNonlocalised(
        outboundDate,
        dateFormats.YEAR_MONTH_DAY_SHORT,
      );
      const formattedInboundDate = inboundDate
        ? formatDateNonlocalised(inboundDate, dateFormats.YEAR_MONTH_DAY_SHORT)
        : null;
      const datePath = formattedInboundDate
        ? `${formattedOutboundDate}/${formattedInboundDate}`
        : formattedOutboundDate;

      const travellerParams = `?adultsv2=${adults ?? 1}${
        childrenAges && childrenAges.length > 0
          ? `&childrenv2=${childrenAges.join('|')}`
          : ''
      }`;

      if (!itineraryId) return null;
      const url = isMobilePhone
        ? `/transport/flights/${origin}/${destination}/${datePath}/#/details/${itineraryId}/index/0${travellerParams}`
        : `/transport/flights/${origin}/${destination}/${datePath}/config/${itineraryId}${travellerParams}`;

      return url;
    },
    [
      isMobilePhone,
      itineraryCardProps,
      inboundDate,
      outboundDate,
      adults,
      childrenAges,
    ],
  );

  const handleCardClick = useCallback(
    (pollingComplete: boolean) => {
      const numbers = getEventNumbers(
        indicativePrice,
        preferDirects,
        dealHunterSession,
      );

      trackInteractionEvent({
        name: 'Itinerary_Clicked',
        strings: {
          ...(tripDuration != null && {
            tripDuration: tripDuration.toString(),
          }),
        },
        numbers,
        bools: { pollingComplete, preferDirect: preferDirects },
      });
    },
    [dealHunterSession, preferDirects, tripDuration, indicativePrice],
  );

  const handleShowDetails = useCallback(
    (id: string | undefined) => {
      handleCardClick(true);

      const url = getRedirectUrl(id);
      if (url) {
        redirect(url, true);
      }
    },
    [
      getRedirectUrl,
      dealHunterSession,
      isMobilePhone,
      handleCardClick,
      preferDirects,
    ],
  );

  return {
    itineraryCardProps,
    showLoading,
    showCard,
    isSessionActive: isRadarSessionActive,
    pollCount,
    shouldShowItineraryCard,
    handleShowDetails,
    handleCardClick,
  };
};
